import React, { FC, ReactNode, useState, useEffect } from 'react'
import { Icon } from '@iconify/react'

import { fetchFile } from '@pepper/utils/src/api/files/files'
import { downloadSubmissionFile } from '@pepper/utils/src/api/assignments/assignments'

import { Spin, FilePreviewV2, Tooltip } from '../../atoms'
import { useShowMessage } from '../../providers'

import styles from './_.module.css'

interface Props {
  attachments: Attachment[]
}

interface SingleAttachmentProps {
  file: Attachment
}

interface AssignmentGoogleDocAttachmentProps {
  assignmentTitle?: string
  fileUrl: string
  assignmentId?: string
  fallbackImage: ReactNode
  downloadFormat?: 'docx' | 'pdf'
  type?: 'link' | 'button'
  onClick?: () => void
  loading?: boolean
  downloadable?: boolean
  preview?: string
}

interface FilePreviewProps {
  file: Attachment
  fallbackImage: ReactNode
}

export const Attachments: FC<Props> = ({ attachments = [] }) => {
  return (
    <div className={styles.attachmentsContainer}>
      <div className={styles.attachments}>
        {Array.isArray(attachments) &&
          attachments.map(file => (
            <>
              <FilePreviewV2 id={file.id} name={file.name} />
            </>
          ))}
      </div>
    </div>
  )
}

export const SingleAttachment: FC<SingleAttachmentProps> = ({ file }) => {
  const showMessage = useShowMessage()

  const handleView = async (id: string) => {
    try {
      const response = await fetchFile({ id })
      const fileObjectURL = URL.createObjectURL(response.data)
      window.open(fileObjectURL)
    } catch (error) {
      showMessage({ type: 'error', message: 'Could not open file.' })
    }
  }

  const downloadFile = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | null,
    id: string,
    fileName: string
  ) => {
    e && e.preventDefault()
    try {
      const response = await fetchFile({ id })
      const fileObjectURL = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = fileObjectURL
      a.download = fileName
      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click()
      a.remove() //afterwards we remove the element again
    } catch (error) {
      showMessage({ type: 'error', message: 'Could not open file.' })
    }
  }

  return (
    <div key={file.id} className={styles.fileBlock}>
      <div
        className={styles.fileName}
        onClick={e => handleView(file.id)}
        onKeyPress={e => handleView(file.id)}
        tabIndex={0}
        role="button"
      >
        <Icon icon="lucide:file-text" width="18" height="18" />
        <Tooltip title={file.name}>
          <span className={styles.text}>{file.name}</span>
        </Tooltip>
      </div>
      <div className={styles.downloadIcon}>
        <a
          href={file.id}
          target="_blank"
          rel="noreferrer"
          onClick={e => downloadFile(e, file.id, file.name)}
        >
          <Icon icon="lucide:download-cloud" width="18" height="18" />
        </a>
      </div>
    </div>
  )
}

export const AssignmentGoogleDocAttachment: FC<
  AssignmentGoogleDocAttachmentProps
> = ({
  assignmentTitle = '',
  fileUrl,
  assignmentId,
  fallbackImage,
  downloadFormat = 'docx',
  type = 'link',
  onClick,
  loading = false,
  downloadable = true,
  preview
}) => {
  const showMessage = useShowMessage()
  const [isDownloading, setDownloading] = useState<boolean>(false)

  const downloadAssignmentFile = async () => {
    try {
      setDownloading(true)
      const response = await downloadSubmissionFile({
        id: assignmentId ?? '',
        format: downloadFormat
      })
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `Assignment.docx`
      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click()
      a.remove() //afterwards we remove the element again
    } catch (error) {
      showMessage({ type: 'error', message: 'Could not open file.' })
    } finally {
      setDownloading(false)
    }
  }

  const getDownloadOption = () => {
    if (isDownloading) {
      return <Icon icon="ant-design:loading-outlined" />
    }
    return (
      <Icon
        icon="lucide:download"
        width="14"
        height="14"
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          downloadAssignmentFile()
        }}
      />
    )
  }

  const WrapperComponent: FC<{
    type: 'link' | 'button'
    redirectUrl: string
    onClick?: () => void
    preview?: string
  }> = ({
    children,
    type: wrapperType = 'link',
    redirectUrl,
    preview,
    onClick: handleOnClick = () => {
      // do nothing
    }
  }) => {
    if (wrapperType === 'link') {
      return (
        <a
          href={preview ?? redirectUrl}
          target="_blank"
          rel="noreferrer"
          onClick={e => e.stopPropagation()}
        >
          {children}
        </a>
      )
    } else {
      return (
        <div
          className={styles.wrapperSection}
          onClick={e => {
            e.stopPropagation()
            handleOnClick()
          }}
          onKeyPress={e => {
            e.stopPropagation()
            handleOnClick()
          }}
          role="button"
          tabIndex={0}
        >
          {children}
        </div>
      )
    }
  }

  return (
    <div className={styles.fileContainer}>
      {preview ? (
        <div className={styles.previewContainer}>
          <img src={preview} alt="preview" className={styles.previewImage} />
        </div>
      ) : (
        <div className={styles.thumbnail}>
          {fallbackImage}
          <Tooltip title={assignmentTitle ?? ''}>
            <span className={styles.assignmentFileName}>
              {assignmentTitle ?? ''}
            </span>
          </Tooltip>
        </div>
      )}
      {/* Hover component */}
      <WrapperComponent
        preview={preview}
        redirectUrl={fileUrl}
        type={type}
        onClick={onClick}
      >
        <div className={styles.hoverOptionsContainer}>
          {downloadable ? (
            <div className={styles.hoverHeader}>
              {assignmentId ? (
                <div className={styles.headerOptions}>
                  {getDownloadOption()}
                </div>
              ) : (
                ''
              )}
            </div>
          ) : (
            <div></div>
          )}
          <div className={styles.hoverFooter}>
            <div>
              <span>Click to open</span>{' '}
              {loading ? (
                <Icon icon="ant-design:loading-outlined" />
              ) : (
                <Icon icon="lucide:external-link" width="14" height="14" />
              )}
            </div>
          </div>
        </div>
      </WrapperComponent>
    </div>
  )
}

export const FilePreview: FC<FilePreviewProps> = ({ file, fallbackImage }) => {
  const showMessage = useShowMessage()
  const [isDownloading, setDownloading] = useState<boolean>(false)
  const [previewImage, setPreviewImage] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (file.thumbnail_available && file.id) {
      const fetchThumbnail = async () => {
        try {
          const response = await fetchFile({ id: file.id, showThumbnail: true })

          if (response.status === 'ok' && response.data) {
            const thumbnail = URL.createObjectURL(response.data)

            setPreviewImage(thumbnail)
          }
        } catch (error) {
          showMessage({ type: 'error', message: error })
        }
      }
      fetchThumbnail()
    }
  }, [file.id, file.thumbnail_available, showMessage])

  const handleView = async (id: string) => {
    setIsLoading(true)
    try {
      const response = await fetchFile({ id })
      const fileObjectURL = URL.createObjectURL(response.data)
      window.open(fileObjectURL)
    } catch (error) {
      showMessage({ type: 'error', message: 'Could not open file.' })
    } finally {
      setIsLoading(false)
    }
  }

  const downloadFile = async (id: string, fileName: string) => {
    setDownloading(true)
    try {
      const response = await fetchFile({ id })
      const fileObjectURL = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = fileObjectURL
      a.download = fileName
      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click()
      a.remove() //afterwards we remove the element again
    } catch (error) {
      showMessage({ type: 'error', message: 'Could not open file.' })
    } finally {
      setDownloading(false)
    }
  }

  const getDownloadOption = () => {
    if (isDownloading) {
      return <Icon icon="ant-design:loading-outlined" />
    }
    return (
      <Icon
        icon="lucide:download"
        width="14"
        height="14"
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          downloadFile(file.id, file.name)
        }}
      />
    )
  }

  return (
    <Spin spinning={isLoading} size="small">
      <div className={styles.fileContainer}>
        {previewImage === '' ? (
          <div className={styles.thumbnail}>
            {fallbackImage}
            <Tooltip title={file?.name ?? ''}>
              <span className={styles.assignmentFileName}>
                {file?.name ?? ''}
              </span>
            </Tooltip>
          </div>
        ) : (
          <div className={styles.thumbnail}>
            <img src={previewImage} alt={file.name} />
          </div>
        )}
        {/* Hover component */}
        <div
          onClick={() => handleView(file.id)}
          onKeyPress={() => handleView(file.id)}
          role="button"
          tabIndex={0}
        >
          <div className={styles.hoverOptionsContainer}>
            <div className={styles.hoverHeader}>
              <div className={styles.headerOptions}>{getDownloadOption()}</div>
            </div>
            <div className={styles.hoverFooter}>
              <div>
                {previewImage === '' ? (
                  <React.Fragment>
                    <span>Click to open</span>{' '}
                    <Icon icon="lucide:external-link" width="14" height="14" />
                  </React.Fragment>
                ) : (
                  <div>
                    <span>{file.name}</span>
                    <Icon icon="lucide:external-link" width="14" height="14" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  )
}
